import { useEffect } from "react";
import classNames from "classnames";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";

import { Box, Checkbox, IconButton, Typography, styled } from "~/common/mui";
import { type NotificationContext, type NotificationData, Notifications } from "../../Messages";
import { useBoolMap } from "~/common/hooks/useBoolMap";
import { LinkButton } from "~/common/components/Link";
import { useTranslation } from "react-i18next";
import DraftsIcon from "@mui/icons-material/Drafts";
import DeleteIcon from "@mui/icons-material/Delete";
import MailIcon from "@mui/icons-material/Mail";

const ListRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("tablet")]: {
    width: "100vw",
    position: "relative",
    left: "50%",
    right: "50%",
    marginLeft: "-50vw",
    marginRight: "-50vw",
  },
}));

const ListHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(4),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
  [theme.breakpoints.up("tablet")]: {
    borderBottom: `3px solid ${theme.palette.divider}`,
    padding: `0 ${theme.spacing(3)} ${theme.spacing(1)}`,
  },
}));

const ItemRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ theme, unread }) => ({
  display: "grid",
  columnGap: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: `${theme.spacing(2)} ${theme.spacing(2)} ${theme.spacing(0.5)}`,
  gridTemplateColumns: "auto 1fr",
  ...(unread && {
    background: theme.palette.surface.main,
  }),
  [theme.breakpoints.up("desktop-s")]: {
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    gridTemplateColumns: "auto 13.5% minmax(10px, 1fr) 8.9% 6.4%",
  },
}));

interface NotificationsTableProps {
  data: NotificationData[];
  className?: string;
  onDeleteItems: (uid: string | string[]) => void;
  onMarkAsRead: (uid: string | string[], isRead: boolean) => void;
}

export const NotificationsList = ({ data, className, onDeleteItems, onMarkAsRead }: NotificationsTableProps) => {
  const { t } = useTranslation();

  const context: NotificationContext = {
    markAsRead: async (item, isRead = true) => {
      if (item.read !== isRead) {
        onMarkAsRead(item.uid, isRead);
      }
    },
  };

  const { map: selectedItems, toggle: setSelected, replace } = useBoolMap();

  const dataUid = data.map((_) => _.uid).join("-");
  useEffect(() => {
    replace({});
  }, [replace, dataUid]);

  const enableButtons = Object.values(selectedItems).some((val) => val);

  const isAllSelected = data.length > 0 && data.every((item) => selectedItems[item.uid]);
  const isAnyChecked = enableButtons;

  const toggleSelectAll = () => {
    const newVal = !isAllSelected;
    const newSelected = data.map((item) => item.uid);
    setSelected(newSelected, newVal);
  };
  const readSelected = () => {
    const selectedUids = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([uid, _]) => uid);
    onMarkAsRead(selectedUids, true);
  };
  const deleteSelected = () => {
    const selectedUids = Object.entries(selectedItems)
      .filter(([_, selected]) => selected)
      .map(([uid, _]) => uid);
    onDeleteItems(selectedUids);
  };

  return (
    <ListRoot className={classNames(className)}>
      <ListHeader>
        <Checkbox
          checked={isAnyChecked}
          indeterminate={isAnyChecked && !isAllSelected}
          onChange={toggleSelectAll}
          data-test-id={"checkbox-notifications-check-all"}
        />
        <LinkButton
          iconBefore={<DraftsIcon  />}
          data-test-id={"read-selected"}
          disabled={!enableButtons}
          onClick={readSelected}
        >
          {t("notificationsPage.actions.markAsRead")}
        </LinkButton>
        <LinkButton
          iconBefore={<DeleteIcon  />}
          data-test-id={"delete-selected"}
          disabled={!enableButtons}
          onClick={deleteSelected}
        >
          {t("notificationsPage.actions.delete")}
        </LinkButton>
      </ListHeader>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {data.map((row, index) => (
          <Item
            key={row.uid}
            data={row}
            context={context}
            selected={selectedItems[row.uid] ?? false}
            onDelete={() => onDeleteItems(row.uid)}
            onSelect={(newVal) => setSelected(row.uid, newVal)}
          />
        ))}
      </Box>
    </ListRoot>
  );
};

interface ItemProps {
  data: NotificationData;
  context: NotificationContext;
  selected: boolean;
  onSelect: (newValue: boolean) => void;
  onDelete: () => void;
}

const Item = ({ selected, data, context, onSelect, onDelete }: ItemProps) => {
  const { t } = useTranslation();

  return (
    <ItemRow unread={!data.read}>
      <Box sx={{ gridColumn: "1 / 2" }} data-test-id={"notification-selected"}>
        <Checkbox checked={selected} onChange={(e) => onSelect(e.target.checked)} sx={{ mt: -1 }} />
      </Box>
      <Typography
        variant="bodyMedium"
        sx={{
          gridColumn: { phone: "2 / 3", "desktop-s": "2 / 3" },
          marginBottom: { phone: 1, "desktop-s": 0 },
        }}
        data-test-id={"notification-type"}
      >
        {t(data.type)}
      </Typography>
      <Typography
        variant="bodyMedium"
        sx={{
          gridColumn: { phone: "2 / 3", "desktop-s": "3 / 4" },
          marginBottom: { phone: 0.5, "desktop-s": 0 },
          fontWeight: "bold",
          wordBreak: "break-word",
        }}
        data-test-id={"notification-title"}
      >
        {Notifications.renderTitle(data, context)}
      </Typography>
      <Typography
        variant="bodyMedium"
        sx={{
          gridColumn: { phone: "2 / 3", "desktop-s": "3 / 4" },
          marginBottom: { phone: 0.5, "desktop-s": 0 },
          wordBreak: "break-word",
        }}
        data-test-id={"notification-text"}
      >
        {Notifications.renderDescription(data, context)}
      </Typography>
      <Typography
        variant="bodyMedium"
        sx={{
          gridColumn: { phone: "2 / 3", "desktop-s": "4 / 5" },
          gridRow: { "desktop-s": "1 / 2" },
          textAlign: "right",
        }}
      >
        {formatDate(new Date(data.notificationDate), DateFormatType.DATE)}
      </Typography>
      <Box
        sx={{
          display: { phone: "none", "desktop-s": "flex" },
          gridRow: { "desktop-s": "1 / 2" },
          gridColumn: { "desktop-s": "5 / 6" },
          justifySelf: "flex-end",
          gap: 2,
          alignItems: "flex-start",
          "& > *": { margin: "-12px" },
        }}
      >
        <IconButton
          color={"primary"}
          onClick={() => context.markAsRead(data, !data.read)}
          data-test-id={"read-notification-btn"}
        >
          {data.read ? <MailIcon  /> : <DraftsIcon  />}
        </IconButton>
        <IconButton color={"primary"} onClick={onDelete} data-test-id={"delete-notification-btn"}>
          <DeleteIcon  />
        </IconButton>
      </Box>
    </ItemRow>
  );
};
